(() => {
  const FINDER_VERSION = 8;
  const STANDARD_ANIMATION_FACTOR = 0.5;

  class AzulejoFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        window.sessionStorage.setItem('azullado_finder_visited_v1', '1');
      } catch (error) {
        // Der Finder funktioniert auch, wenn Session Storage blockiert ist.
      }

      this.stage = this.querySelector('[data-finder-stage]');
      this.progress = this.querySelector('[data-finder-progress]');
      this.progressText = this.querySelector('[data-finder-progress-text]');
      this.progressBar = this.querySelector('[data-finder-progress-bar]');
      this.progressValue = this.querySelector('[data-finder-progress-value]');
      this.announcement = this.querySelector('[data-finder-announcement]');
      this.storageKey = this.dataset.storageKey;
      this.participantLimit = Number.parseInt(this.dataset.participantLimit || '11', 10);
      this.collectionConfigured = this.dataset.collectionConfigured === 'true';
      this.choiceLabel = this.dataset.choiceLabel || 'Das mag ich mehr';
      this.addLabel = this.dataset.addLabel || 'Dieses Azulejo-Wandbild darf einziehen';
      this.addingLabel = this.dataset.addingLabel || 'Wird hinzugefügt …';
      this.addedLabel = this.dataset.addedLabel || 'Dein Wandbild liegt im Warenkorb';
      this.cartError = this.dataset.cartError || 'Das Produkt konnte nicht hinzugefügt werden.';
      this.continueShoppingUrl = this.dataset.continueShoppingUrl || '/collections/gerahmte-azulejos';
      this.locale = this.dataset.locale || document.documentElement.lang || 'de';
      this.copy = this.readCopy();
      this.startCopy = this.readJson('[data-finder-start-copy]', {});
      this.factsHtml = this.querySelector('[data-finder-facts]')?.innerHTML.trim() || '';
      this.rooms = this.readJson('[data-finder-rooms]', []);
      this.sectionRoot = this.closest('.az-finder-section') || this.parentElement;
      this.emailFormHost = this.sectionRoot?.querySelector('[data-finder-email-form-host]') || null;
      this.emailFormApp = this.emailFormHost?.querySelector('[data-finder-email-form-app]') || null;
      this.emailFormAvailable = Boolean(this.emailFormApp);
      this.products = this.readJson('[data-finder-products]', [])
        .map((product) => ({
          ...product,
          shortTitle: this.getShortTitle(product.fullTitle),
          images: Array.isArray(product.images) && product.images.length
            ? product.images
            : [{ src: product.image, alt: product.imageAlt || product.fullTitle }]
        }))
        .filter((product) => product.id && product.variantId && product.image);

      this.handleClick = this.handleClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('submit', this.handleSubmit);
      this.emailFormHost?.addEventListener('submit', this.handleSubmit, true);
      this.initializeEmailForm();

      this.initializeState();
      this.track('Finder geöffnet', window.location.pathname);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('submit', this.handleSubmit);
      this.emailFormHost?.removeEventListener('submit', this.handleSubmit, true);
      this.emailFormObserver?.disconnect();
      this.emailFormRootObservers?.forEach((observer) => observer.disconnect());
    }

    readJson(selector, fallback) {
      const node = this.querySelector(selector);
      if (!node) return fallback;

      try {
        return JSON.parse(node.textContent.trim() || JSON.stringify(fallback));
      } catch (error) {
        console.error('Azulejo-Finder: JSON konnte nicht gelesen werden.', error);
        return fallback;
      }
    }

    readCopy() {
      const template = this.querySelector('[data-finder-copy]');
      const copy = {};
      if (!template) return copy;

      template.content.querySelectorAll('[data-copy]').forEach((node) => {
        copy[node.dataset.copy] = node.textContent.trim();
      });
      return copy;
    }

    formatTemplate(template = '', values = {}) {
      return Object.entries(values).reduce((result, [key, value]) => {
        const safeValue = String(value ?? '');
        const bracketPattern = new RegExp(`\\[\\s*${key}\\s*\\]`, 'g');
        const legacyLiquidPattern = new RegExp(`{{\\s*${key}\\s*}}`, 'g');

        return result
          .replace(bracketPattern, safeValue)
          .replace(legacyLiquidPattern, safeValue);
      }, String(template));
    }

    getShortTitle(fullTitle = '') {
      const normalized = String(fullTitle).replace(/\s+/g, ' ').trim();
      const quoted = normalized.match(/[“„"]([^”"“]+)[”"“]/u);
      if (quoted?.[1]) return quoted[1].trim();

      return normalized
        .replace(/^Azulejo[-\s]?Wandbild\s*/iu, '')
        .split(/\s+[–—-]\s+/u)[0]
        .replace(/^[“„"]|[”"“]$/gu, '')
        .trim();
    }

    createInitialState() {
      return {
        version: FINDER_VERSION,
        participantIds: [],
        completedRounds: 0,
        favoriteId: null,
        heartTotal: 0,
        heartInherited: 0,
        heartFresh: 0,
        stage: 'start',
        roomId: null,
        addedToCart: false
      };
    }

    initializeState() {
      if (this.products.length < 2) {
        this.state = null;
        return;
      }

      const productMap = new Map(this.products.map((product) => [String(product.id), product]));
      const stored = this.readStoredState();

      if (stored && this.isValidState(stored, productMap)) {
        this.state = stored;
        return;
      }

      this.state = this.createInitialState();
      this.saveState();
    }

    startFinder() {
      const participants = this.shuffle([...this.products])
        .slice(0, Math.min(this.participantLimit, this.products.length))
        .map((product) => String(product.id));

      this.state = {
        ...this.createInitialState(),
        participantIds: participants,
        stage: 'duel'
      };
      this.saveState();
      this.track('Finder gestartet', 'Azulejo-Finder', participants.length, {
        participantIds: participants
      });
      this.render();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    readStoredState() {
      try {
        const stored = sessionStorage.getItem(this.storageKey);
        return stored ? JSON.parse(stored) : null;
      } catch (error) {
        return null;
      }
    }

    isValidState(state, productMap) {
      if (!state || state.version !== FINDER_VERSION) return false;
      if (!['start', 'duel', 'room', 'result'].includes(state.stage)) return false;
      if (!Array.isArray(state.participantIds)) return false;
      if (state.stage === 'start') return state.participantIds.length === 0;
      if (state.participantIds.length < 2) return false;
      if (!state.participantIds.every((id) => productMap.has(String(id)))) return false;
      if (state.completedRounds < 0 || state.completedRounds > state.participantIds.length - 1) return false;
      if (state.favoriteId && !productMap.has(String(state.favoriteId))) return false;
      return true;
    }

    saveState() {
      if (!this.state) return;
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (error) {
        // The finder still works when session storage is blocked.
      }
    }

    shuffle(items) {
      for (let index = items.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [items[index], items[randomIndex]] = [items[randomIndex], items[index]];
      }
      return items;
    }

    get participantProducts() {
      if (!this.state) return [];
      const productMap = new Map(this.products.map((product) => [String(product.id), product]));
      return this.state.participantIds.map((id) => productMap.get(String(id))).filter(Boolean);
    }

    get favorite() {
      if (!this.state?.favoriteId) return null;
      return this.products.find((product) => String(product.id) === String(this.state.favoriteId)) || null;
    }

    get totalRounds() {
      return Math.max(1, this.participantProducts.length - 1);
    }

    get currentPair() {
      const participants = this.participantProducts;
      if (this.state.completedRounds === 0) {
        return { left: participants[0], right: participants[1] };
      }

      return {
        left: this.favorite,
        right: participants[this.state.completedRounds + 1]
      };
    }

    setView(stage) {
      this.dataset.view = stage;
    }

    scrollToStage() {
      const target = this.stage || this;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          target.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });
    }

    render() {
      this.stage.setAttribute('aria-busy', 'false');

      if (!this.state) {
        const message = !this.collectionConfigured
          ? this.copy['missing-collection']
          : this.copy['not-enough-products'];
        this.setView('empty');
        this.renderEmpty(message);
        this.syncEmailFormVisibility();
        return;
      }

      this.setView(this.state.stage);
      this.updateProgress();

      if (this.state.stage === 'start') {
        this.renderStart();
      } else if (this.state.stage === 'room') {
        this.renderRoomQuestion();
      } else if (this.state.stage === 'result') {
        this.renderResult();
      } else {
        this.renderDuel();
      }

      this.syncEmailFormVisibility();
    }

    syncEmailFormVisibility() {
      if (!this.emailFormHost) return;
      const shouldShow = Boolean(
        this.emailFormAvailable &&
        this.state?.stage === 'result'
      );

      this.emailFormHost.classList.toggle('is-visible', shouldShow);
      this.emailFormHost.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      this.emailFormHost.inert = !shouldShow;

      if (shouldShow) {
        this.updateEmailFormContext();
        this.customizeEmailForm();
        window.requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
          window.setTimeout(() => {
            this.customizeEmailForm();
            window.dispatchEvent(new Event('resize'));
          }, 350);
          [800, 1600].forEach((delay) => {
            window.setTimeout(() => this.customizeEmailForm(), delay);
          });
        });
      }
    }

    initializeEmailForm() {
      if (!this.emailFormHost) return;

      this.emailFormRootObservers = [];
      this.emailFormInteractionRoots = new WeakSet();
      this.emailFormObserver = new MutationObserver(() => {
        this.observeEmailFormRoots();
        this.customizeEmailForm();
      });
      this.emailFormObserver.observe(this.emailFormHost, {
        childList: true,
        subtree: true
      });
      this.observeEmailFormRoots();
      this.customizeEmailForm();
    }

    observeEmailFormRoots() {
      this.emailFormRootObservers?.forEach((observer) => observer.disconnect());
      this.emailFormRootObservers = [];

      this.getEmailFormRoots().forEach((root) => {
        this.bindEmailFormInteractionRoot(root);
        if (!(root instanceof ShadowRoot)) return;
        const observer = new MutationObserver(() => this.customizeEmailForm());
        observer.observe(root, {
          childList: true,
          subtree: true,
          characterData: true
        });
        this.emailFormRootObservers.push(observer);
      });
    }

    bindEmailFormInteractionRoot(root) {
      if (!root || this.emailFormInteractionRoots?.has(root)) return;
      this.emailFormInteractionRoots?.add(root);

      const syncBeforeSubmission = (event) => {
        const target = event.target;
        const isSubmitEvent = event.type === 'submit';
        const isSubmitControl = Boolean(target?.closest?.('button[type="submit"], input[type="submit"]'));
        const isEnterSubmit = event.type === 'keydown' && event.key === 'Enter';
        if (!isSubmitEvent && !isSubmitControl && !isEnterSubmit) return;

        this.updateEmailFormContext();
        this.getEmailFormRoots().forEach((formRoot) => this.syncEmailFormFields(formRoot));
      };

      ['pointerdown', 'mousedown', 'touchstart', 'click', 'keydown', 'submit'].forEach((eventName) => {
        root.addEventListener?.(eventName, syncBeforeSubmission, true);
      });
    }

    getEmailFormRoots() {
      if (!this.emailFormApp) return [];
      const roots = [this.emailFormApp];
      const visited = new Set();

      for (let index = 0; index < roots.length; index += 1) {
        const root = roots[index];
        if (!root || visited.has(root)) continue;
        visited.add(root);

        root.querySelectorAll?.('*').forEach((element) => {
          if (element.shadowRoot && !visited.has(element.shadowRoot)) {
            roots.push(element.shadowRoot);
          }
        });
      }

      return roots;
    }

    customizeEmailForm() {
      if (!this.emailFormHost) return;

      this.getEmailFormRoots().forEach((root) => {
        this.syncEmailFormFields(root);

        const firstControl = root.querySelector?.('input, textarea, select');
        if (firstControl) {
          root.querySelectorAll?.('h1, h2, h3, h4, [role="heading"], p').forEach((node) => {
            const position = node.compareDocumentPosition(firstControl);
            const comesBeforeControl = Boolean(position & Node.DOCUMENT_POSITION_FOLLOWING);
            if (comesBeforeControl) {
              node.style.setProperty('display', 'none', 'important');
              node.setAttribute('aria-hidden', 'true');
            }
          });
        }

        root.querySelectorAll?.('button[type="submit"], input[type="submit"]').forEach((button) => {
          button.style.setProperty('background', 'var(--color-primary-button-background, #2f349f)', 'important');
          button.style.setProperty('background-color', 'var(--color-primary-button-background, #2f349f)', 'important');
          button.style.setProperty('border-color', 'var(--color-primary-button-background, #2f349f)', 'important');
          button.style.setProperty('color', 'var(--color-primary-button-text, #ffffff)', 'important');
        });

        this.styleEmailSuccessState(root);
      });
    }

    getEmailFormContextValues() {
      if (!this.favorite || this.state?.stage !== 'result') return null;
      const winner = this.favorite;
      const room = this.rooms.find((item) => item.id === this.state.roomId) || null;

      return {
        source: 'azulejo-finder',
        productId: String(winner.id || ''),
        productHandle: winner.handle || '',
        productTitle: winner.fullTitle || '',
        productUrl: this.absoluteUrl(winner.url),
        room: room?.label || '',
        locale: this.locale || document.documentElement.lang || 'de'
      };
    }

    normalizeEmailFieldText(value = '') {
      return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[–—−]/g, '-')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    emailFieldDescriptor(control, root) {
      const parts = [
        control.name,
        control.id,
        control.placeholder,
        control.getAttribute?.('aria-label'),
        control.getAttribute?.('data-name'),
        control.getAttribute?.('data-field-name'),
        control.getAttribute?.('data-metafield-key')
      ];

      if (control.id) {
        try {
          const escapedId = window.CSS?.escape ? window.CSS.escape(control.id) : control.id.replace(/(["'\\.#:[\]()])/g, '\\$1');
          root.querySelectorAll?.(`label[for="${escapedId}"]`).forEach((label) => parts.push(label.textContent));
        } catch (error) {
          // The remaining descriptors are sufficient when the generated ID cannot be queried.
        }
      }

      const wrappingLabel = control.closest?.('label');
      if (wrappingLabel) parts.push(wrappingLabel.textContent);

      let parent = control.parentElement;
      for (let depth = 0; parent && depth < 3; depth += 1, parent = parent.parentElement) {
        parts.push(parent.getAttribute?.('data-name'));
        parts.push(parent.getAttribute?.('data-field-name'));
        parts.push(parent.getAttribute?.('aria-label'));
        const directLabel = Array.from(parent.children || []).find((child) => child.tagName === 'LABEL');
        if (directLabel) parts.push(directLabel.textContent);
      }

      return this.normalizeEmailFieldText(parts.filter(Boolean).join(' '));
    }

    matchEmailTechnicalField(descriptor) {
      const matches = (...terms) => terms.some((term) => descriptor.includes(term));

      if (matches('azullado finder source', 'finder source', 'finder quelle', 'finder_source')) return 'source';
      if (matches('azullado finder product id', 'finder product id', 'finder produkt id', 'finder_product_id')) return 'productId';
      if (matches('azullado finder product handle', 'finder product handle', 'finder produkt handle', 'finder_product_handle')) return 'productHandle';
      if (matches('azullado finder product title', 'finder product title', 'finder produkttitel', 'finder produkt titel', 'finder_product_title')) return 'productTitle';
      if (matches('azullado finder product url', 'finder product url', 'finder produkt url', 'finder_product_url')) return 'productUrl';
      if (matches('azullado finder room', 'finder room', 'finder raum', 'finder_room')) return 'room';
      if (matches('azullado finder locale', 'finder locale', 'finder language', 'finder sprache', 'finder_locale')) return 'locale';
      return null;
    }

    setEmailFormControlValue(control, value) {
      const nextValue = String(value ?? '');
      const view = control.ownerDocument?.defaultView || window;

      const setValueProperty = (element, property, propertyValue) => {
        if (!element) return;
        let prototype = element;
        let descriptor = null;
        while (prototype && !descriptor) {
          descriptor = Object.getOwnPropertyDescriptor(prototype, property);
          prototype = Object.getPrototypeOf(prototype);
        }
        try {
          if (descriptor?.set) descriptor.set.call(element, propertyValue);
          else element[property] = propertyValue;
        } catch (error) {
          try { element[property] = propertyValue; } catch (nestedError) { /* Ignore read-only properties. */ }
        }
      };

      const dispatchValueEvents = (element) => {
        if (!element?.dispatchEvent) return;
        try {
          element.dispatchEvent(new view.InputEvent('input', {
            bubbles: true,
            composed: true,
            inputType: 'insertText',
            data: nextValue
          }));
        } catch (error) {
          element.dispatchEvent(new view.Event('input', { bubbles: true, composed: true }));
        }
        element.dispatchEvent(new view.Event('change', { bubbles: true, composed: true }));
        element.dispatchEvent(new view.Event('blur', { bubbles: true, composed: true }));
      };

      // Force controlled frameworks such as React to notice a programmatic change.
      try {
        if (control._valueTracker?.setValue) {
          control._valueTracker.setValue(nextValue === '' ? '__azullado__' : '');
        }
      } catch (error) {
        // Continue with the standards-based value setters.
      }

      setValueProperty(control, 'value', nextValue);
      setValueProperty(control, 'defaultValue', nextValue);
      control.setAttribute?.('value', nextValue);
      dispatchValueEvents(control);

      // Shopify Forms renders fields through custom elements in a shadow DOM.
      // Synchronise the owning field component as well as its native input.
      let owner = control.parentElement || control.getRootNode?.()?.host || null;
      const visited = new Set([control]);
      for (let depth = 0; owner && depth < 8; depth += 1) {
        if (visited.has(owner)) break;
        visited.add(owner);

        const isCustomElement = owner.tagName?.includes('-');
        const hasValueProperty = 'value' in owner || 'defaultValue' in owner;
        if (isCustomElement || hasValueProperty) {
          setValueProperty(owner, 'value', nextValue);
          setValueProperty(owner, 'defaultValue', nextValue);
          owner.setAttribute?.('value', nextValue);
          dispatchValueEvents(owner);
        }

        const parent = owner.parentElement;
        owner = parent || owner.getRootNode?.()?.host || null;
      }
    }

    hideEmailTechnicalField(control, root) {
      const form = control.closest?.('form');
      const wrappingLabel = control.closest?.('label');
      let wrapper = wrappingLabel || control.parentElement;
      let current = wrapper;

      for (let depth = 0; current?.parentElement && depth < 5; depth += 1) {
        const parent = current.parentElement;
        if (parent === form || parent === root || parent.tagName === 'FORM') break;
        const controls = parent.querySelectorAll?.('input:not([type="hidden"]), textarea, select') || [];
        if (controls.length !== 1 || controls[0] !== control) break;
        wrapper = parent;
        current = parent;
      }

      [wrapper, wrappingLabel].filter(Boolean).forEach((node) => {
        node.style.setProperty('position', 'absolute', 'important');
        node.style.setProperty('width', '1px', 'important');
        node.style.setProperty('height', '1px', 'important');
        node.style.setProperty('margin', '-1px', 'important');
        node.style.setProperty('padding', '0', 'important');
        node.style.setProperty('overflow', 'hidden', 'important');
        node.style.setProperty('clip', 'rect(0 0 0 0)', 'important');
        node.style.setProperty('clip-path', 'inset(50%)', 'important');
        node.style.setProperty('white-space', 'nowrap', 'important');
        node.style.setProperty('border', '0', 'important');
        node.style.setProperty('opacity', '0', 'important');
        node.style.setProperty('pointer-events', 'none', 'important');
        node.setAttribute('aria-hidden', 'true');
        node.setAttribute('data-az-finder-technical-field', '');
      });

      if (control.id) {
        try {
          const escapedId = window.CSS?.escape ? window.CSS.escape(control.id) : control.id.replace(/(["'\\.#:[\]()])/g, '\\$1');
          root.querySelectorAll?.(`label[for="${escapedId}"]`).forEach((label) => {
            label.style.setProperty('position', 'absolute', 'important');
            label.style.setProperty('width', '1px', 'important');
            label.style.setProperty('height', '1px', 'important');
            label.style.setProperty('overflow', 'hidden', 'important');
            label.style.setProperty('clip-path', 'inset(50%)', 'important');
            label.setAttribute('aria-hidden', 'true');
          });
        } catch (error) {
          // No separate label needs to be hidden when the generated ID cannot be queried.
        }
      }

      control.tabIndex = -1;
      control.setAttribute('aria-hidden', 'true');
    }

    syncEmailFormFields(root) {
      const values = this.getEmailFormContextValues();
      if (!root || !values) return 0;

      let matched = 0;
      root.querySelectorAll?.('input:not([type="hidden"]), textarea, select').forEach((control) => {
        const descriptor = this.emailFieldDescriptor(control, root);
        const key = this.matchEmailTechnicalField(descriptor);
        if (!key) return;

        this.setEmailFormControlValue(control, values[key]);
        this.hideEmailTechnicalField(control, root);
        matched += 1;
      });

      if (matched) this.emailFormHost?.setAttribute('data-finder-fields-synced', String(matched));
      return matched;
    }

    isVisibleEmailElement(element) {
      if (!element || element.hidden || element.getAttribute?.('aria-hidden') === 'true') return false;
      if (element.matches?.('input[type="hidden"]')) return false;
      const styles = window.getComputedStyle(element);
      return styles.display !== 'none' && styles.visibility !== 'hidden';
    }

    styleEmailSuccessState(root) {
      const controls = Array.from(root.querySelectorAll?.(
        'input:not([type="hidden"]), textarea, select, button[type="submit"], input[type="submit"]'
      ) || []);
      if (controls.some((control) => this.isVisibleEmailElement(control))) return;

      const candidates = Array.from(root.querySelectorAll?.(
        '[role="status"], [role="alert"], h1, h2, h3, h4, p'
      ) || []).filter((node) => (
        this.isVisibleEmailElement(node) &&
        !node.closest?.('label') &&
        node.textContent.trim().length > 0
      ));
      if (!candidates.length) return;

      // Keep only the actual text elements, not containers that contain them.
      const messages = candidates.filter((node) => !candidates.some((other) => (
        other !== node && node.contains(other)
      )));
      if (!messages.length) return;

      let box = root.querySelector?.('[data-az-finder-success-box]') || null;
      if (!box) {
        const firstMessage = messages[0];
        const doc = firstMessage?.ownerDocument || document;
        box = doc.createElement('div');
        box.setAttribute('data-az-finder-success-box', '');
        const insertionParent = firstMessage?.parentNode;
        if (insertionParent) insertionParent.insertBefore(box, firstMessage);
        messages.forEach((message) => {
          if (message && box) box.appendChild(message);
        });
      }

      const activeMessages = Array.from(box.querySelectorAll('h1, h2, h3, h4, p, [role="status"], [role="alert"]')).filter((node) => (
        this.isVisibleEmailElement(node) && node.textContent.trim().length > 0
      ));

      box.style.setProperty('display', 'block', 'important');
      box.style.setProperty('width', '100%', 'important');
      box.style.setProperty('box-sizing', 'border-box', 'important');
      box.style.setProperty('margin', '0 auto', 'important');
      box.style.setProperty('padding', '18px 22px', 'important');
      box.style.setProperty('border', '0', 'important');
      box.style.setProperty('border-radius', '8px', 'important');
      box.style.setProperty('background', '#f1f2f4', 'important');
      box.style.setProperty('color', '#2A2C5C', 'important');
      box.style.setProperty('text-align', 'center', 'important');
      box.style.setProperty('line-height', '1.5', 'important');
      box.style.setProperty('max-width', '46rem', 'important');

      activeMessages.forEach((node, index) => {
        node.style.setProperty('display', 'block', 'important');
        node.style.setProperty('width', 'auto', 'important');
        node.style.setProperty('margin', index === 0 ? '0' : '8px 0 0', 'important');
        node.style.setProperty('padding', '0', 'important');
        node.style.setProperty('border', '0', 'important');
        node.style.setProperty('background', 'transparent', 'important');
        node.style.setProperty('color', '#2A2C5C', 'important');
      });
    }

    updateEmailFormContext() {
      if (!this.emailFormHost) return;
      const values = this.getEmailFormContextValues();
      if (!values) return;

      Object.assign(this.emailFormHost.dataset, {
        finderSource: values.source,
        finderProductId: values.productId,
        finderProductHandle: values.productHandle,
        finderProductTitle: values.productTitle,
        finderProductUrl: values.productUrl,
        finderRoom: values.room,
        finderLocale: values.locale
      });

      this.getEmailFormRoots().forEach((root) => this.syncEmailFormFields(root));
    }

    renderEmpty(message) {
      this.progress.hidden = true;
      this.stage.innerHTML = `<div class="az-finder__empty"><p>${this.escapeHtml(message || '')}</p></div>`;
    }

    renderStart() {
      const image = this.startCopy.image
        ? `<div class="az-finder__start-media"><img src="${this.escapeAttribute(this.startCopy.image)}" alt="${this.escapeAttribute(this.startCopy.imageAlt || '')}" width="1200" height="1200" loading="eager"></div>`
        : '';

      this.stage.innerHTML = `
        <div class="az-finder__start${image ? '' : ' az-finder__start--without-image'}">
          ${image}
          <div class="az-finder__start-copy">
            ${this.startCopy.heading ? `<h1 class="az-finder__start-heading">${this.escapeHtml(this.startCopy.heading)}</h1>` : ''}
            ${this.startCopy.text ? `<p class="az-finder__start-text">${this.escapeHtml(this.startCopy.text)}</p>` : ''}
            <button class="button az-finder__start-button" type="button" data-finder-start>
              ${this.escapeHtml(this.startCopy.button || 'Jetzt starten')}
            </button>
          </div>
        </div>
      `;
    }

    updateProgress() {
      if (this.state.stage === 'start' || this.state.stage === 'room' || this.state.stage === 'result') {
        this.progress.hidden = true;
        return;
      }

      const percent = this.totalRounds > 0
        ? Math.round((this.state.completedRounds / this.totalRounds) * 100)
        : 0;
      let progressTemplate = this.copy['progress-template'] || '[percent] %';
      if (/\[(seen|total)\]/i.test(progressTemplate)) {
        progressTemplate = '[percent] %';
      }

      this.progress.hidden = false;
      this.progressText.textContent = this.formatTemplate(progressTemplate, { percent });
      this.progressBar.setAttribute('aria-valuenow', String(percent));
      this.progressBar.setAttribute('aria-valuetext', `${percent} %`);
      this.progressValue.style.width = `${percent}%`;
    }

    renderDuel({ entering = false } = {}) {
      const { left, right } = this.currentPair;
      if (!left || !right) {
        this.state.stage = 'room';
        this.saveState();
        this.renderRoomQuestion();
        return;
      }

      const hasFavorite = this.state.completedRounds > 0;
      this.stage.innerHTML = `
        <div class="az-finder__duel${this.state.completedRounds === 0 ? ' az-finder__duel--first' : ''}${entering ? ' is-entering' : ''}" data-finder-duel>
          ${this.cardMarkup(left, 'left', hasFavorite)}
          ${this.cardMarkup(right, 'right', false)}
        </div>
        ${this.restartMarkup()}
      `;

      this.track('Vergleich angezeigt', `${left.shortTitle} | ${right.shortTitle}`, this.state.completedRounds + 1, {
        leftProductId: String(left.id),
        leftProductTitle: left.fullTitle,
        rightProductId: String(right.id),
        rightProductTitle: right.fullTitle
      });
      this.preloadUpcoming();
    }

    heartIconMarkup({ filled = true, numbered = false, count = 0, animation = false } = {}) {
      const classes = [
        'az-finder__heart',
        filled ? 'is-filled' : 'is-outline',
        numbered ? 'is-numbered' : '',
        animation ? 'is-animation-heart' : ''
      ].filter(Boolean).join(' ');
      const countMarkup = numbered
        ? `<span class="az-finder__heart-number"${animation ? ` data-heart-count-target="${count}"` : ''}>${animation ? '1' : this.escapeHtml(String(count))}</span>`
        : '';

      return `
        <span class="${classes}" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false"><path d="M20.8 4.9a5.5 5.5 0 0 0-7.8 0L12 5.9l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21.5l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8Z"/></svg>
          ${countMarkup}
        </span>
      `;
    }

    heartSegmentMarkup(count, filled, { grouped = true, animation = false } = {}) {
      const safeCount = Math.max(0, Number.parseInt(count || '0', 10));
      if (!safeCount) return '';

      if (grouped && safeCount >= 5) {
        return this.heartIconMarkup({ filled, numbered: true, count: safeCount, animation });
      }

      return Array.from({ length: safeCount }, () => this.heartIconMarkup({
        filled,
        animation
      })).join('');
    }

    heartDisplayMarkup(total, inherited = 0, fresh = total, { grouped = true, animation = false } = {}) {
      const safeTotal = Math.max(0, Number.parseInt(total || '0', 10));
      const safeInherited = Math.min(
        safeTotal,
        Math.max(0, Number.parseInt(inherited || '0', 10))
      );
      const safeFresh = Math.max(0, safeTotal - safeInherited);

      // From five onward, all hearts are represented by one numbered heart.
      // The compact result badge is always filled. The distinction between
      // inherited and self-earned hearts remains visible during the animation.
      if (grouped && safeTotal >= 5) {
        return `
          <span class="az-finder__heart-row${animation ? ' is-animation-row' : ''}">
            ${this.heartIconMarkup({
              filled: true,
              numbered: true,
              count: safeTotal,
              animation
            })}
          </span>
        `;
      }

      return `
        <span class="az-finder__heart-row${animation ? ' is-animation-row' : ''}">
          ${this.heartSegmentMarkup(safeInherited, false, { grouped, animation })}
          ${this.heartSegmentMarkup(safeFresh, true, { grouped, animation })}
        </span>
      `;
    }

    heartAnimationSequenceMarkup(total, inherited = 0) {
      const safeTotal = Math.max(1, Math.min(9, Number.parseInt(total || '1', 10)));
      const safeInherited = Math.min(
        safeTotal,
        Math.max(0, Number.parseInt(inherited || '0', 10))
      );
      const fillForIndex = (index) => index >= safeInherited;

      if (safeTotal < 5) {
        const hearts = Array.from({ length: safeTotal }, (_, index) => `
          <span class="az-finder__animation-heart-item" data-heart-simple>
            ${this.heartIconMarkup({ filled: fillForIndex(index), animation: true })}
          </span>
        `).join('');

        return `
          <span class="az-finder__animation-heart-sequence is-simple" data-heart-sequence>
            ${hearts}
          </span>
        `;
      }

      const bottomCount = Math.max(0, safeTotal - 5);
      const mergeOrderIndices = [
        1,
        2,
        4,
        5,
        ...Array.from({ length: bottomCount }, (_, index) => index + 6)
      ];

      // If inherited hearts exist, the central collector represents one of them
      // and therefore starts outlined. All remaining inherited hearts are assigned
      // to the beginning of the merge order. The filled hearts follow afterwards.
      const anchorFilled = safeInherited === 0;
      const remainingInherited = Math.max(0, safeInherited - (anchorFilled ? 0 : 1));
      const filledByItemIndex = new Map([[3, anchorFilled]]);
      mergeOrderIndices.forEach((itemIndex, mergeIndex) => {
        filledByItemIndex.set(itemIndex, mergeIndex >= remainingInherited);
      });

      const topRow = Array.from({ length: 5 }, (_, index) => {
        const itemIndex = index + 1;
        const isAnchor = itemIndex === 3;
        return `
          <span
            class="az-finder__animation-heart-item${isAnchor ? ' is-anchor' : ' is-source'}"
            data-heart-item
            data-heart-index="${itemIndex}"
            ${isAnchor ? `data-heart-anchor data-heart-count-target="${safeTotal}"` : 'data-heart-source'}
          >
            ${this.heartIconMarkup({
              filled: Boolean(filledByItemIndex.get(itemIndex)),
              numbered: isAnchor,
              count: isAnchor ? 1 : 0,
              animation: true
            })}
          </span>
        `;
      }).join('');

      const bottomRow = bottomCount ? Array.from({ length: bottomCount }, (_, index) => {
        const itemIndex = index + 6;
        return `
          <span
            class="az-finder__animation-heart-item is-source"
            data-heart-item
            data-heart-index="${itemIndex}"
            data-heart-source
          >
            ${this.heartIconMarkup({
              filled: Boolean(filledByItemIndex.get(itemIndex)),
              animation: true
            })}
          </span>
        `;
      }).join('') : '';

      return `
        <span class="az-finder__animation-heart-sequence is-merge-all" data-heart-sequence data-heart-total="${safeTotal}">
          <span class="az-finder__animation-heart-row is-row-top">
            ${topRow}
          </span>
          ${bottomCount ? `<span class="az-finder__animation-heart-row is-row-bottom">${bottomRow}</span>` : ''}
        </span>
      `;
    }

    favoriteHeartsMarkup() {
      const total = Math.max(1, Number.parseInt(this.state.heartTotal || '1', 10));
      const inherited = Math.max(0, Number.parseInt(this.state.heartInherited || '0', 10));
      const label = inherited > 0
        ? `${total} Herzen Favoritenstärke, davon ${inherited} übernommen`
        : `${total} Herzen Favoritenstärke`;

      return `
        <span class="az-finder__favorite-hearts" role="img" aria-label="${this.escapeAttribute(label)}">
          ${this.heartDisplayMarkup(total, inherited, total - inherited)}
        </span>
      `;
    }

    cardMarkup(product, side, isFavorite) {
      const favoriteHearts = isFavorite ? this.favoriteHeartsMarkup() : '';

      return `
        <article class="az-finder__card ${isFavorite ? 'az-finder__card--favorite' : 'az-finder__card--challenger'}" data-finder-card data-side="${side}">
          <div class="az-finder__heart-slot" aria-hidden="${isFavorite ? 'false' : 'true'}">
            ${favoriteHearts}
          </div>
          <div class="az-finder__media" data-finder-media>
            <img
              class="az-finder__image"
              src="${this.escapeAttribute(product.image)}"
              alt="${this.escapeAttribute(product.imageAlt || product.fullTitle)}"
              loading="${this.state.completedRounds === 0 ? 'eager' : 'lazy'}"
              width="1600"
              height="1600"
            >
          </div>
          <h2 class="az-finder__title">${this.escapeHtml(product.shortTitle)}</h2>
          <button class="button az-finder__choice" type="button" data-finder-choice="${side}" aria-label="${this.escapeAttribute(`${product.shortTitle}: ${this.choiceLabel}`)}">
            ${this.escapeHtml(this.choiceLabel)}
          </button>
        </article>
      `;
    }

    restartMarkup() {
      const label = this.copy['restart-label'] || 'Auswahl neu starten';
      return `
        <div class="az-finder__reload">
          <span class="az-finder__reload-label">Reload</span>
          <button class="az-finder__restart" type="button" data-finder-restart aria-label="${this.escapeAttribute(label)}" title="${this.escapeAttribute(label)}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.9 7.6A8 8 0 1 1 4 12"/><path d="M4 4v4h4"/></svg>
          </button>
        </div>
      `;
    }

    renderRoomQuestion() {
      const winner = this.favorite;
      if (!winner) return;
      this.setView('room');

      const roomButtons = this.rooms.length
        ? this.rooms.map((room) => `
            <button class="button az-finder__room-button" type="button" data-finder-room="${this.escapeAttribute(room.id)}">
              ${this.escapeHtml(room.label)}
            </button>
          `).join('')
        : '';

      this.stage.innerHTML = `
        <div class="az-finder__conversion az-finder__conversion--room">
          <div class="az-finder__room-winner">
            <div class="az-finder__winner-media">
              <span class="az-finder__winner-heart" role="img" aria-label="${this.escapeAttribute(`${this.state.heartTotal} Herzen Favoritenstärke`)}">
                ${this.heartIconMarkup({ filled: true })}
              </span>
              <img src="${this.escapeAttribute(winner.image)}" alt="${this.escapeAttribute(winner.imageAlt || winner.fullTitle)}" width="1600" height="1600">
            </div>
            <h2 class="az-finder__winner-title">${this.escapeHtml(winner.shortTitle)}</h2>
          </div>
          <div class="az-finder__conversion-copy">
            <p class="az-finder__eyebrow">${this.escapeHtml(this.copy['room-eyebrow'] || '')}</p>
            <h2 class="az-finder__conversion-heading">${this.escapeHtml(this.copy['room-heading'] || '')}</h2>
            <p class="az-finder__conversion-text">${this.escapeHtml(this.copy['room-text'] || '')}</p>
            <div class="az-finder__rooms">${roomButtons}</div>
          </div>
        </div>
        ${this.restartMarkup()}
      `;
      this.announce(`${winner.shortTitle} ist dein Favorit.`);
    }

    scarcityMarkup(product) {
      if (!product.inventoryTracked) return '';
      const quantity = Number(product.inventoryQuantity);
      if (!Number.isFinite(quantity) || quantity <= 0) return '';

      let message = this.copy['scarcity-low'] || '';
      if (quantity === 1) message = this.copy['scarcity-one'] || message;
      if (quantity === 2) message = this.copy['scarcity-two'] || message;
      if (quantity === 3) message = this.copy['scarcity-three'] || message;

      return message
        ? `<p class="az-finder__scarcity">${this.escapeHtml(message)}</p>`
        : '';
    }

    roomSceneMarkup(winner, room) {
      const background = room?.backgroundImage
        ? `<img class="az-finder__room-background" src="${this.escapeAttribute(room.backgroundImage)}" alt="" loading="eager">`
        : '';
      const placeholderClass = room?.backgroundImage ? '' : ' az-finder__room-scene--placeholder';

      return `
        <div class="az-finder__room-scene${placeholderClass}">
          ${background}
          <div class="az-finder__room-artwork" aria-hidden="true">
            <img src="${this.escapeAttribute(winner.image)}" alt="" width="1600" height="1600">
          </div>
        </div>
      `;
    }

    galleryMarkup(product) {
      const images = product.images?.length
        ? product.images
        : [{ src: product.image, alt: product.imageAlt || product.fullTitle }];
      const first = images[0];
      const hasMultiple = images.length > 1;
      const thumbs = hasMultiple
        ? `<div class="az-finder__gallery-thumbs" role="tablist" aria-label="Produktbilder">
            ${images.map((image, index) => `
              <button class="az-finder__gallery-thumb${index === 0 ? ' is-active' : ''}" type="button" data-finder-gallery-thumb="${index}" aria-label="Bild ${index + 1} anzeigen" aria-selected="${index === 0 ? 'true' : 'false'}">
                <img src="${this.escapeAttribute(image.src)}" alt="" width="180" height="180" loading="lazy">
              </button>
            `).join('')}
          </div>`
        : '';

      return `
        <div class="az-finder__gallery" data-finder-gallery data-index="0">
          <div class="az-finder__gallery-main">
            <img data-finder-gallery-main src="${this.escapeAttribute(first.src)}" alt="${this.escapeAttribute(first.alt || product.fullTitle)}" width="1800" height="1800">
            ${hasMultiple ? `
              <button class="az-finder__gallery-nav az-finder__gallery-nav--prev" type="button" data-finder-gallery-prev aria-label="Vorheriges Produktbild">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>
              </button>
              <button class="az-finder__gallery-nav az-finder__gallery-nav--next" type="button" data-finder-gallery-next aria-label="Nächstes Produktbild">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>
              </button>
            ` : ''}
          </div>
          ${thumbs}
        </div>
      `;
    }

    renderResult() {
      const winner = this.favorite;
      if (!winner) return;
      this.setView('result');
      this.galleryIndex = 0;
      this.resultImages = winner.images?.length
        ? winner.images
        : [{ src: winner.image, alt: winner.imageAlt || winner.fullTitle }];

      const room = this.rooms.find((item) => item.id === this.state.roomId) || this.rooms[0] || null;
      const roomPhrase = room?.resultPhrase || room?.label || '';
      const resultSentence = this.formatTemplate(
        this.copy['result-sentence'] || 'Das Wandbild „[product]“ [room] ist eine gute Idee.',
        { product: winner.shortTitle, room: roomPhrase }
      );
      const productUrl = this.absoluteUrl(winner.url);
      const cartUrl = `${window.Shopify?.routes?.root || '/'}cart`;
      const continueShoppingUrl = this.absoluteUrl(this.continueShoppingUrl);
      const roomLabel = room?.label || '';
      const addedClass = this.state.addedToCart ? ' is-added' : '';
      const addText = this.state.addedToCart ? this.addedLabel : this.addLabel;

      this.stage.innerHTML = `
        <div class="az-finder__result">
          <header class="az-finder__result-intro">
            <h1 class="az-finder__result-heading">${this.escapeHtml(this.copy['result-heading'] || '')}</h1>
            <p class="az-finder__result-sentence">${this.escapeHtml(resultSentence)}</p>
          </header>

          <div class="az-finder__mockup-wrap">
            ${this.roomSceneMarkup(winner, room)}
            ${this.copy['mockup-note'] ? `<p class="az-finder__mockup-note">${this.escapeHtml(this.copy['mockup-note'])}</p>` : ''}
          </div>

          <section class="az-finder__product-result" aria-labelledby="az-finder-product-${this.escapeAttribute(String(winner.id))}">
            <div class="az-finder__actual-product">
              ${this.galleryMarkup(winner)}
            </div>

            <div class="az-finder__product-copy">
              <h2 class="az-finder__product-title" id="az-finder-product-${this.escapeAttribute(String(winner.id))}">${this.escapeHtml(winner.fullTitle)}</h2>
              ${winner.descriptionHtml ? `<div class="az-finder__description rte">${winner.descriptionHtml}</div>` : ''}
              ${this.factsHtml ? `<div class="az-finder__facts rte">${this.factsHtml}</div>` : ''}

              <div class="az-finder__purchase">
                <p class="az-finder__price">${this.escapeHtml(winner.price)}</p>
                ${this.scarcityMarkup(winner)}

                <div class="az-finder__actions">
                  <button class="button az-finder__add${addedClass}" type="button" data-finder-add data-variant-id="${this.escapeAttribute(String(winner.variantId))}" ${this.state.addedToCart ? 'disabled' : ''}>
                    ${this.state.addedToCart ? '<span aria-hidden="true">✓</span>' : ''}${this.escapeHtml(addText)}
                  </button>
                  <div class="az-finder__cart-success" data-finder-cart-success ${this.state.addedToCart ? '' : 'hidden'}>
                    <a href="${this.escapeAttribute(cartUrl)}" data-finder-cart-link>${this.escapeHtml(this.copy['cart-link-label'] || 'Zum Warenkorb')}</a>
                    <a href="${this.escapeAttribute(continueShoppingUrl)}" data-finder-continue-shopping>${this.escapeHtml(this.copy['continue-shopping-label'] || 'Weiter einkaufen')}</a>
                  </div>
                </div>
                <p class="az-finder__error" data-finder-error hidden></p>
              </div>

              <div class="az-finder__secondary-actions">
                <a class="az-finder__details" href="${this.escapeAttribute(productUrl)}" data-finder-product-details ${this.state.addedToCart ? 'hidden' : ''}>${this.escapeHtml(this.copy['details-label'] || '')}</a>
                <button class="az-finder__share" type="button" data-finder-share>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="18" cy="5" r="2.5"/><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="19" r="2.5"/><path d="m8.2 10.8 7.5-4.4M8.2 13.2l7.5 4.4"/></svg>
                  <span data-finder-share-label>${this.escapeHtml(this.copy['share-label'] || 'Ergebnis teilen')}</span>
                </button>
              </div>
            </div>
          </section>


          ${this.restartMarkup()}
        </div>
      `;

      this.updateEmailFormContext();

      if (this.lastTrackedResultId !== String(winner.id)) {
        this.lastTrackedResultId = String(winner.id);
        this.track('Ergebnis angezeigt', winner.shortTitle, undefined, {
          productId: String(winner.id),
          productTitle: winner.fullTitle,
          room: roomLabel
        });
        this.track('E-Mail-Bereich angezeigt', winner.shortTitle, undefined, {
          productId: String(winner.id),
          productTitle: winner.fullTitle,
          room: roomLabel
        });
        this.dispatchEvent(new CustomEvent('azullado:finder-email-context', {
          bubbles: true,
          detail: {
            finder_source: 'azulejo-finder',
            finder_product_id: String(winner.id),
            finder_product_handle: winner.handle || '',
            finder_product_title: winner.fullTitle,
            finder_product_url: productUrl,
            finder_room: roomLabel,
            finder_locale: this.locale
          }
        }));
      }
    }

    async handleClick(event) {
      if (event.target.closest('[data-finder-start]')) {
        this.startFinder();
        return;
      }

      const choice = event.target.closest('[data-finder-choice]');
      if (choice) {
        await this.select(choice.dataset.finderChoice);
        return;
      }

      const roomButton = event.target.closest('[data-finder-room]');
      if (roomButton) {
        this.state.roomId = roomButton.dataset.finderRoom;
        this.state.stage = 'result';
        this.saveState();
        const room = this.rooms.find((item) => item.id === this.state.roomId);
        const winner = this.favorite;
        this.track('Raum gewählt', room?.label || this.state.roomId, undefined, {
          roomId: this.state.roomId,
          roomLabel: room?.label || '',
          productId: String(winner?.id || ''),
          productTitle: winner?.fullTitle || ''
        });
        this.render();
        this.scrollToStage();
        return;
      }

      if (event.target.closest('[data-finder-gallery-prev]')) {
        this.moveGallery(-1);
        return;
      }

      if (event.target.closest('[data-finder-gallery-next]')) {
        this.moveGallery(1);
        return;
      }

      const thumb = event.target.closest('[data-finder-gallery-thumb]');
      if (thumb) {
        this.setGalleryIndex(Number.parseInt(thumb.dataset.finderGalleryThumb, 10));
        return;
      }

      const addButton = event.target.closest('[data-finder-add]');
      if (addButton) {
        await this.addToCart(addButton);
        return;
      }

      const shareButton = event.target.closest('[data-finder-share]');
      if (shareButton) {
        await this.shareResult(shareButton);
        return;
      }

      const detailsLink = event.target.closest('[data-finder-product-details]');
      if (detailsLink) {
        const winner = this.favorite;
        this.track('Produktdetails geöffnet', winner?.shortTitle || 'Unbekanntes Produkt', undefined, {
          productId: String(winner?.id || ''),
          productTitle: winner?.fullTitle || ''
        });
        return;
      }

      const cartLink = event.target.closest('[data-finder-cart-link]');
      if (cartLink) {
        const winner = this.favorite;
        this.track('Warenkorb geöffnet', winner?.shortTitle || 'Unbekanntes Produkt', undefined, {
          productId: String(winner?.id || ''),
          productTitle: winner?.fullTitle || ''
        });
        return;
      }

      const continueLink = event.target.closest('[data-finder-continue-shopping]');
      if (continueLink) {
        const winner = this.favorite;
        this.track('Weiter eingekauft', continueLink.href, undefined, {
          productId: String(winner?.id || ''),
          productTitle: winner?.fullTitle || '',
          targetUrl: continueLink.href
        });
        return;
      }

      if (event.target.closest('[data-finder-restart]')) {
        this.restart();
      }
    }

    handleSubmit(event) {
      const form = event.target.closest?.('form');
      if (!form || !this.emailFormHost?.contains(form)) return;

      this.updateEmailFormContext();
      this.getEmailFormRoots().forEach((root) => this.syncEmailFormFields(root));

      const winner = this.favorite;
      this.track('Mail-Anmeldung gestartet', winner?.shortTitle || 'Unbekanntes Produkt', undefined, {
        productId: String(winner?.id || ''),
        productTitle: winner?.fullTitle || '',
        room: this.rooms.find((room) => room.id === this.state.roomId)?.label || ''
      });

      [350, 900, 1800, 3200].forEach((delay) => {
        window.setTimeout(() => this.customizeEmailForm(), delay);
      });
    }

    moveGallery(direction) {
      if (!Array.isArray(this.resultImages) || this.resultImages.length < 2) return;
      const next = (this.galleryIndex + direction + this.resultImages.length) % this.resultImages.length;
      this.setGalleryIndex(next);
    }

    setGalleryIndex(index) {
      if (!Array.isArray(this.resultImages) || !this.resultImages[index]) return;
      this.galleryIndex = index;
      const image = this.resultImages[index];
      const main = this.querySelector('[data-finder-gallery-main]');
      if (main) {
        main.src = image.src;
        main.alt = image.alt || this.favorite?.fullTitle || '';
      }
      const gallery = this.querySelector('[data-finder-gallery]');
      if (gallery) gallery.dataset.index = String(index);
      this.querySelectorAll('[data-finder-gallery-thumb]').forEach((button) => {
        const active = Number.parseInt(button.dataset.finderGalleryThumb, 10) === index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    async select(side) {
      if (this.animating || this.state.stage !== 'duel') return;
      const { left, right } = this.currentPair;
      const selected = side === 'left' ? left : right;
      const rejected = side === 'left' ? right : left;
      if (!selected || !rejected) return;

      const nextState = { ...this.state };
      const previousHeartTotal = Math.max(
        0,
        Number.parseInt(this.state.heartTotal || String(this.state.completedRounds), 10)
      );
      const retainedFavorite = Boolean(
        this.state.favoriteId && String(selected.id) === String(this.state.favoriteId)
      );

      nextState.completedRounds += 1;
      nextState.favoriteId = String(selected.id);
      nextState.heartTotal = previousHeartTotal + 1;

      if (retainedFavorite) {
        // Inherited hearts stay inherited and therefore remain outlined.
        // Every further win only adds one new, filled heart.
        nextState.heartInherited = Math.min(
          previousHeartTotal,
          Math.max(0, Number.parseInt(this.state.heartInherited || '0', 10))
        );
        nextState.heartFresh = nextState.heartTotal - nextState.heartInherited;
      } else {
        nextState.heartInherited = previousHeartTotal;
        nextState.heartFresh = 1;
      }

      const finalRound = nextState.completedRounds >= this.totalRounds;
      nextState.stage = finalRound ? 'room' : 'duel';

      this.track('Vergleich gewonnen', selected.shortTitle, nextState.heartTotal, {
        selectedProductId: String(selected.id),
        selectedProductTitle: selected.fullTitle,
        rejectedProductId: String(rejected.id),
        rejectedProductTitle: rejected.fullTitle,
        retainedFavorite
      });

      this.track(
        retainedFavorite ? 'Favorit bestätigt' : 'Favorit gewechselt',
        selected.shortTitle,
        nextState.heartTotal,
        {
          selectedProductId: String(selected.id),
          selectedProductTitle: selected.fullTitle,
          inheritedHearts: nextState.heartInherited,
          totalHearts: nextState.heartTotal
        }
      );

      if (nextState.heartTotal > 0 && nextState.heartTotal % 5 === 0) {
        this.track('Herzstufe erreicht', selected.shortTitle, nextState.heartTotal, {
          productId: String(selected.id),
          productTitle: selected.fullTitle,
          heartTotal: nextState.heartTotal
        });
      }

      await this.animateSelection(side, finalRound, nextState, () => {
        this.state = nextState;
        this.saveState();
        this.updateProgress();

        if (finalRound) {
          this.renderRoomQuestion();
          this.track('Finder abgeschlossen', selected.shortTitle, this.totalRounds, {
            productId: String(selected.id),
            productTitle: selected.fullTitle,
            heartTotal: nextState.heartTotal
          });
        } else {
          this.renderDuel({ entering: true });
        }
      });

      if (finalRound) {
        this.scrollToStage();
      }
    }

    delay(milliseconds) {
      return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    }

    standardAnimationTime(milliseconds) {
      return Math.max(0, Math.round(Number(milliseconds || 0) * STANDARD_ANIMATION_FACTOR));
    }

    isHeartFilled(element) {
      const heart = element?.matches?.('.az-finder__heart')
        ? element
        : element?.querySelector?.('.az-finder__heart');
      return Boolean(heart?.classList.contains('is-filled'));
    }

    setHeartFilled(element, filled = true) {
      const heart = element?.matches?.('.az-finder__heart')
        ? element
        : element?.querySelector?.('.az-finder__heart');
      if (!heart) return;
      heart.classList.toggle('is-filled', Boolean(filled));
      heart.classList.toggle('is-outline', !filled);
    }

    async animateHeartSequence(container, { takeover = false } = {}) {
      const sequence = container.querySelector('[data-heart-sequence]');
      if (!sequence || typeof sequence.animate !== 'function') return;

      const simpleHearts = Array.from(sequence.querySelectorAll('[data-heart-simple]'));
      const mergeAll = sequence.classList.contains('is-merge-all');
      const anchor = sequence.querySelector('[data-heart-anchor]');

      if (mergeAll) {
        const items = Array.from(sequence.querySelectorAll('[data-heart-item]'));
        const sources = Array.from(sequence.querySelectorAll('[data-heart-source]'));
        const counter = anchor?.querySelector('.az-finder__heart-number');
        let collectorFilled = this.isHeartFilled(anchor);

        items.forEach((item) => {
          item.style.opacity = '0';
        });

        await container.animate([
          { opacity: 0, transform: 'translate(-50%, -50%) scale(.92)' },
          { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }
        ], {
          duration: this.standardAnimationTime(220),
          easing: 'ease-out',
          fill: 'forwards'
        }).finished;

        if (!anchor) return;

        await Promise.all(items.map((item, index) => item.animate([
          { opacity: 0, transform: 'translateY(8px) scale(.72)' },
          { opacity: 1, transform: 'translateY(0) scale(1.08)', offset: 0.74 },
          { opacity: 1, transform: 'translateY(0) scale(1)' }
        ], {
          duration: this.standardAnimationTime(300),
          delay: this.standardAnimationTime(index * 55),
          easing: 'cubic-bezier(.2,.9,.25,1)',
          fill: 'forwards'
        }).finished));

        items.forEach((item) => {
          item.style.opacity = '1';
        });
        anchor.style.zIndex = '4';

        // First show every heart at exactly the same size. Then the third heart
        // grows while moving from the row into the true centre of the sequence.
        await this.delay(this.standardAnimationTime(210));
        const sequenceRect = sequence.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();
        const moveX = (sequenceRect.left + sequenceRect.width / 2) - (anchorRect.left + anchorRect.width / 2);
        const moveY = (sequenceRect.top + sequenceRect.height / 2) - (anchorRect.top + anchorRect.height / 2);
        const collectorScale = window.innerWidth < 750 ? 1.52 : 1.68;
        const collectorTransform = `translate(${moveX}px, ${moveY}px) scale(${collectorScale})`;

        await anchor.animate([
          { transform: 'translate(0, 0) scale(1)' },
          { transform: collectorTransform }
        ], {
          duration: this.standardAnimationTime(430),
          easing: 'cubic-bezier(.22,.8,.3,1)',
          fill: 'forwards'
        }).finished;
        anchor.style.transform = collectorTransform;

        await this.delay(this.standardAnimationTime(170));

        const sourceByIndex = new Map(sources.map((item) => [
          Number.parseInt(item.dataset.heartIndex || '0', 10),
          item
        ]));
        const topOrder = [1, 2, 4, 5];
        const bottomOrder = Array.from({
          length: Math.max(0, Number.parseInt(sequence.dataset.heartTotal || '5', 10) - 5)
        }, (_, index) => index + 6);
        const order = [...topOrder, ...bottomOrder]
          .map((index) => sourceByIndex.get(index))
          .filter(Boolean);

        const anchorTargetCenterX = anchorRect.left + anchorRect.width / 2 + moveX;
        const anchorTargetCenterY = anchorRect.top + anchorRect.height / 2 + moveY;
        let count = 1;

        for (const item of order) {
          const sourceFilled = this.isHeartFilled(item);
          const itemRect = item.getBoundingClientRect();
          const itemCenterX = itemRect.left + itemRect.width / 2;
          const itemCenterY = itemRect.top + itemRect.height / 2;
          const translateX = anchorTargetCenterX - itemCenterX;
          const translateY = anchorTargetCenterY - itemCenterY;

          await item.animate([
            { opacity: 1, transform: 'translate(0, 0) scale(1)' },
            { opacity: 1, transform: `translate(${translateX * 0.72}px, ${translateY * 0.72}px) scale(.86)`, offset: 0.72 },
            { opacity: 0, transform: `translate(${translateX}px, ${translateY}px) scale(.28)` }
          ], {
            duration: this.standardAnimationTime(takeover ? 270 : 240),
            easing: 'cubic-bezier(.42,0,.58,1)',
            fill: 'forwards'
          }).finished;

          // Keep the source heart's layout space so the centred collector cannot drift
          // when the surrounding hearts disappear one after another.
          item.style.opacity = '0';
          item.style.visibility = 'hidden';
          item.style.pointerEvents = 'none';

          // The collector stays outlined while it only absorbs inherited hearts.
          // It becomes filled as soon as the first self-earned heart merges into it.
          if (sourceFilled && !collectorFilled) {
            this.setHeartFilled(anchor, true);
            collectorFilled = true;
          }

          count += 1;
          if (counter) counter.textContent = String(count);

          await anchor.animate([
            { transform: collectorTransform },
            { transform: `translate(${moveX}px, ${moveY}px) scale(${collectorScale * 1.16})`, offset: 0.45 },
            { transform: `translate(${moveX}px, ${moveY}px) scale(${collectorScale * 0.97})`, offset: 0.76 },
            { transform: collectorTransform }
          ], {
            duration: this.standardAnimationTime(210),
            easing: 'cubic-bezier(.2,.9,.25,1)',
            fill: 'forwards'
          }).finished;

          anchor.style.transform = collectorTransform;
          await this.delay(this.standardAnimationTime(35));
        }

        await this.delay(this.standardAnimationTime(takeover ? 230 : 200));
        return;
      }

      const mergeSources = Array.from(sequence.querySelectorAll('[data-heart-merge-source]'));
      const countUp = sequence.querySelector('[data-heart-countup]');

      [...simpleHearts, ...mergeSources].forEach((item) => {
        item.style.opacity = '0';
      });
      if (anchor) anchor.style.opacity = '0';

      await container.animate([
        { opacity: 0, transform: 'translate(-50%, -50%) scale(.92)' },
        { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }
      ], {
        duration: this.standardAnimationTime(180),
        easing: 'ease-out',
        fill: 'forwards'
      }).finished;

      await this.delay(this.standardAnimationTime(70));

      if (simpleHearts.length) {
        await Promise.all(simpleHearts.map((item, index) => item.animate([
          { opacity: 0, transform: 'translateY(7px) scale(.68)' },
          { opacity: 1, transform: 'translateY(0) scale(1.12)', offset: 0.72 },
          { opacity: 1, transform: 'translateY(0) scale(1)' }
        ], {
          duration: this.standardAnimationTime(takeover ? 430 : 400),
          delay: this.standardAnimationTime(index * 150),
          easing: 'cubic-bezier(.2,.9,.25,1)',
          fill: 'forwards'
        }).finished));
        await this.delay(this.standardAnimationTime(250));
        return;
      }

      if (!anchor) return;

      await anchor.animate([
        { opacity: 0, transform: 'scale(.72)' },
        { opacity: 1, transform: 'scale(1.14)', offset: 0.68 },
        { opacity: 1, transform: 'scale(1)' }
      ], {
        duration: this.standardAnimationTime(takeover ? 300 : 270),
        easing: 'cubic-bezier(.2,.9,.25,1)',
        fill: 'forwards'
      }).finished;
      anchor.style.opacity = '1';

      const counter = anchor.querySelector('.az-finder__heart-number');

      if (countUp) {
        const target = Number.parseInt(countUp.dataset.heartCountTarget || '0', 10);
        const current = Number.parseInt(counter?.textContent || String(Math.max(1, target - 1)), 10);

        await this.delay(this.standardAnimationTime(110));
        if (counter && target > current) counter.textContent = String(target);
        await anchor.animate([
          { transform: 'scale(1)' },
          { transform: 'scale(1.24)', offset: 0.46 },
          { transform: 'scale(.96)', offset: 0.75 },
          { transform: 'scale(1)' }
        ], {
          duration: this.standardAnimationTime(takeover ? 470 : 420),
          easing: 'cubic-bezier(.2,.9,.25,1)',
          fill: 'forwards'
        }).finished;
        await this.delay(this.standardAnimationTime(160));
        return;
      }

      mergeSources.forEach((item) => {
        item.style.opacity = '1';
      });

      const anchorRect = anchor.getBoundingClientRect();
      const anchorCenterX = anchorRect.left + (anchorRect.width / 2);

      for (let index = 0; index < mergeSources.length; index += 1) {
        const item = mergeSources[index];
        const itemRect = item.getBoundingClientRect();
        const itemCenterX = itemRect.left + (itemRect.width / 2);
        const translateX = anchorCenterX - itemCenterX;

        await item.animate([
          { opacity: 1, transform: 'translateX(0) scale(1)' },
          { opacity: 1, transform: `translateX(${translateX * 0.72}px) scale(.88)`, offset: 0.72 },
          { opacity: 0, transform: `translateX(${translateX}px) scale(.35)` }
        ], {
          duration: this.standardAnimationTime(takeover ? 220 : 195),
          easing: 'cubic-bezier(.42,0,.58,1)',
          fill: 'forwards'
        }).finished;

        item.style.display = 'none';
        if (counter) counter.textContent = String(index + 2);

        anchor.animate([
          { transform: 'scale(1)' },
          { transform: 'scale(1.12)', offset: 0.5 },
          { transform: 'scale(1)' }
        ], {
          duration: this.standardAnimationTime(145),
          easing: 'ease-out'
        });
        await this.delay(this.standardAnimationTime(40));
      }

      await this.delay(this.standardAnimationTime(takeover ? 220 : 190));
    }

    createAnimationBackdrop() {
      const backdrop = document.createElement('div');
      backdrop.className = 'az-finder__animation-backdrop';
      backdrop.setAttribute('aria-hidden', 'true');
      document.body.appendChild(backdrop);
      return backdrop;
    }

    winnerOrbitPositions() {
      return [
        { x: -0.03, y: 0.18 },
        { x: -0.05, y: 0.55 },
        { x: 0.05, y: 0.94 },
        { x: 0.28, y: 1.05 },
        { x: 0.58, y: 1.06 },
        { x: 0.9, y: 0.96 },
        { x: 1.04, y: 0.67 },
        { x: 1.05, y: 0.28 },
        { x: 0.82, y: -0.05 },
        { x: 0.4, y: -0.07 }
      ];
    }

    async animateFinalWinner(side, nextState, updateView) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duel = this.querySelector('[data-finder-duel]');
      const selectedMedia = this.querySelector(`[data-side="${side}"] [data-finder-media]`);

      if (reduceMotion || !duel || !selectedMedia || typeof selectedMedia.animate !== 'function') {
        updateView();
        return;
      }

      this.animating = true;
      duel.classList.add('is-selecting');
      this.stage.style.visibility = 'hidden';
      const backdrop = this.createAnimationBackdrop();

      const start = selectedMedia.getBoundingClientRect();
      const clone = selectedMedia.cloneNode(true);
      clone.classList.add('az-finder__animation-clone', 'is-final-winner');
      clone.setAttribute('aria-hidden', 'true');
      Object.assign(clone.style, {
        left: `${start.left}px`,
        top: `${start.top}px`,
        width: `${start.width}px`,
        height: `${start.height}px`
      });
      document.body.appendChild(clone);

      const centerWidth = Math.min(
        window.innerWidth < 750 ? window.innerWidth * 0.64 : window.innerWidth * 0.36,
        start.width * 1.24
      );
      const centerHeight = centerWidth * (start.height / start.width);
      const centerLeft = (window.innerWidth - centerWidth) / 2;
      const viewportCenterTop = (window.innerHeight - centerHeight) / 2;
      const centerTop = Math.max(72, Math.min(viewportCenterTop, window.innerHeight - centerHeight - 54));

      await clone.animate([
        { left: `${start.left}px`, top: `${start.top}px`, width: `${start.width}px`, height: `${start.height}px`, transform: 'scale(1)' },
        { left: `${centerLeft}px`, top: `${centerTop}px`, width: `${centerWidth}px`, height: `${centerHeight}px`, transform: 'scale(1.035)' }
      ], {
        duration: 360,
        easing: 'cubic-bezier(.22,.8,.3,1)',
        fill: 'forwards'
      }).finished;

      const total = Math.max(1, Number.parseInt(nextState.heartTotal || String(this.totalRounds), 10));
      const inherited = Math.min(
        total,
        Math.max(0, Number.parseInt(nextState.heartInherited || '0', 10))
      );
      const filledForIndex = (index) => index >= inherited;
      const positions = this.winnerOrbitPositions();
      const orbit = document.createElement('div');
      orbit.className = 'az-finder__winner-orbit';
      orbit.setAttribute('aria-hidden', 'true');
      Object.assign(orbit.style, {
        left: `${centerLeft}px`,
        top: `${centerTop}px`,
        width: `${centerWidth}px`,
        height: `${centerHeight}px`
      });

      orbit.innerHTML = positions.map((position, index) => `
        <span class="az-finder__winner-orbit-heart${index === 0 ? ' is-collector' : ''}" data-winner-heart="${index}" style="--heart-x:${position.x};--heart-y:${position.y};">
          ${this.heartIconMarkup({
            filled: filledForIndex(index),
            numbered: index === 0,
            count: index === 0 ? 1 : 0
          })}
        </span>
      `).join('');
      document.body.appendChild(orbit);

      await orbit.animate([
        { opacity: 0, transform: 'scale(.92)' },
        { opacity: 1, transform: 'scale(1.03)', offset: 0.72 },
        { opacity: 1, transform: 'scale(1)' }
      ], {
        duration: 310,
        easing: 'cubic-bezier(.2,.9,.25,1)',
        fill: 'forwards'
      }).finished;

      const collector = orbit.querySelector('.is-collector');
      const counter = collector?.querySelector('.az-finder__heart-number');
      let collectorFilled = this.isHeartFilled(collector);
      const targets = Array.from(orbit.querySelectorAll('[data-winner-heart]')).slice(1);
      const segmentDuration = Math.max(115, Math.floor(1280 / Math.max(1, targets.length)));

      for (let index = 0; index < targets.length; index += 1) {
        const target = targets[index];
        const targetFilled = this.isHeartFilled(target);
        const position = positions[index + 1];
        const move = collector.animate([
          { left: collector.style.left || `${positions[index].x * 100}%`, top: collector.style.top || `${positions[index].y * 100}%` },
          { left: `${position.x * 100}%`, top: `${position.y * 100}%` }
        ], {
          duration: segmentDuration,
          easing: 'linear',
          fill: 'forwards'
        });
        const collect = target.animate([
          { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
          { opacity: 0, transform: 'translate(-50%, -50%) scale(.25)' }
        ], {
          duration: Math.max(90, segmentDuration * 0.78),
          delay: segmentDuration * 0.22,
          easing: 'ease-in',
          fill: 'forwards'
        });
        await Promise.all([move.finished, collect.finished]);
        collector.style.left = `${position.x * 100}%`;
        collector.style.top = `${position.y * 100}%`;

        if (targetFilled && !collectorFilled) {
          this.setHeartFilled(collector, true);
          collectorFilled = true;
        }

        if (counter) counter.textContent = String(Math.min(total, index + 2));
      }

      const finalPosition = { x: 0.04, y: -0.04 };
      await collector.animate([
        { left: collector.style.left, top: collector.style.top },
        { left: `${finalPosition.x * 100}%`, top: `${finalPosition.y * 100}%` }
      ], {
        duration: 180,
        easing: 'cubic-bezier(.22,.8,.3,1)',
        fill: 'forwards'
      }).finished;
      collector.style.left = `${finalPosition.x * 100}%`;
      collector.style.top = `${finalPosition.y * 100}%`;
      // The numbered heart in the upper-left final position is always filled.
      this.setHeartFilled(collector, true);
      collectorFilled = true;
      if (counter) counter.textContent = String(total);

      await collector.animate([
        { transform: 'translate(-50%, -50%) scale(1)' },
        { transform: 'translate(-50%, -50%) scale(1.25)', offset: 0.45 },
        { transform: 'translate(-50%, -50%) scale(.96)', offset: 0.72 },
        { transform: 'translate(-50%, -50%) scale(1)' }
      ], {
        duration: 420,
        easing: 'cubic-bezier(.2,.9,.25,1)',
        fill: 'forwards'
      }).finished;

      if (counter) {
        await counter.animate([
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 0, transform: 'scale(.72)' }
        ], {
          duration: 150,
          easing: 'ease-out',
          fill: 'forwards'
        }).finished;
      }

      updateView();
      await Promise.all([
        orbit.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 210, fill: 'forwards' }).finished,
        clone.animate([
          { opacity: 1, transform: 'scale(1.035)' },
          { opacity: 0, transform: 'scale(.98)' }
        ], { duration: 230, easing: 'ease', fill: 'forwards' }).finished,
        backdrop.animate([
          { opacity: 1 },
          { opacity: 1, offset: 0.35 },
          { opacity: 0 }
        ], { duration: 260, easing: 'ease', fill: 'forwards' }).finished
      ]);
      orbit.remove();
      clone.remove();
      backdrop.remove();
      this.stage.style.visibility = '';
      this.animating = false;
    }

    async animateSelection(side, finalRound, nextState, updateView) {
      if (finalRound) {
        await this.animateFinalWinner(side, nextState, updateView);
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duel = this.querySelector('[data-finder-duel]');
      const selectedMedia = this.querySelector(`[data-side="${side}"] [data-finder-media]`);

      if (reduceMotion || !duel || !selectedMedia || typeof selectedMedia.animate !== 'function') {
        updateView();
        return;
      }

      this.animating = true;
      duel.classList.add('is-selecting');
      this.stage.style.visibility = 'hidden';
      const backdrop = this.createAnimationBackdrop();

      const start = selectedMedia.getBoundingClientRect();
      const clone = selectedMedia.cloneNode(true);
      clone.classList.add('az-finder__animation-clone');
      clone.setAttribute('aria-hidden', 'true');
      Object.assign(clone.style, {
        left: `${start.left}px`,
        top: `${start.top}px`,
        width: `${start.width}px`,
        height: `${start.height}px`
      });
      document.body.appendChild(clone);

      const takeover = nextState.heartInherited > 0;
      const centerWidth = Math.min(
        window.innerWidth < 750 ? window.innerWidth * 0.58 : window.innerWidth * 0.34,
        start.width * 1.18
      );
      const centerHeight = centerWidth * (start.height / start.width);
      const centerLeft = (window.innerWidth - centerWidth) / 2;
      const viewportCenterTop = (window.innerHeight - centerHeight) / 2;
      const centerTop = Math.max(70, Math.min(viewportCenterTop, window.innerHeight - centerHeight - 40));

      await clone.animate([
        { left: `${start.left}px`, top: `${start.top}px`, width: `${start.width}px`, height: `${start.height}px`, transform: 'scale(1)' },
        { left: `${centerLeft}px`, top: `${centerTop}px`, width: `${centerWidth}px`, height: `${centerHeight}px`, transform: 'scale(1.025)' }
      ], {
        duration: this.standardAnimationTime(takeover ? 500 : 450),
        easing: 'cubic-bezier(.22,.8,.3,1)',
        fill: 'forwards'
      }).finished;

      await this.delay(this.standardAnimationTime(takeover ? 100 : 80));

      const heart = document.createElement('div');
      heart.className = 'az-finder__animation-hearts';
      heart.setAttribute('aria-hidden', 'true');
      heart.innerHTML = this.heartAnimationSequenceMarkup(
        nextState.heartTotal,
        nextState.heartInherited
      );
      Object.assign(heart.style, {
        left: `${centerLeft + centerWidth / 2}px`,
        top: `${centerTop + centerHeight / 2}px`
      });
      document.body.appendChild(heart);

      await this.animateHeartSequence(heart, { takeover });

      heart.remove();
      updateView();

      const newDuel = this.querySelector('[data-finder-duel]');
      if (newDuel) {
        newDuel.classList.remove('is-entering');
        newDuel.classList.add('is-arriving');
      }

      const target = this.querySelector('[data-side="left"] [data-finder-media]')?.getBoundingClientRect();
      if (target) {
        await clone.animate([
          { left: `${centerLeft}px`, top: `${centerTop}px`, width: `${centerWidth}px`, height: `${centerHeight}px`, opacity: 1 },
          { left: `${target.left}px`, top: `${target.top}px`, width: `${target.width}px`, height: `${target.height}px`, opacity: 1 }
        ], {
          duration: this.standardAnimationTime(takeover ? 500 : 450),
          easing: 'cubic-bezier(.22,.8,.3,1)',
          fill: 'forwards'
        }).finished;
      }

      clone.remove();
      this.stage.style.visibility = '';
      if (newDuel) {
        newDuel.classList.remove('is-arriving');
        void newDuel.offsetWidth;
        newDuel.classList.add('is-entering');
      }
      await backdrop.animate([
        { opacity: 1 },
        { opacity: 0 }
      ], { duration: this.standardAnimationTime(170), easing: 'ease-out', fill: 'forwards' }).finished;
      backdrop.remove();
      this.animating = false;
    }

    preloadUpcoming() {
      if (!this.state || this.state.stage !== 'duel') return;
      const participants = this.participantProducts;
      const indexes = [this.state.completedRounds + 1, this.state.completedRounds + 2];
      indexes.forEach((index) => {
        const product = participants[index];
        if (!product?.image) return;
        const image = new Image();
        image.src = product.image;
      });
    }

    notifyCartUpdated(cart, winner, variantId) {
      const itemCount = Number.parseInt(cart?.item_count || '0', 10);
      const detail = {
        resource: cart,
        sourceId: this.id || 'azulejo-finder',
        data: {
          source: 'azulejo-finder',
          itemCount,
          productId: String(winner?.id || ''),
          variantId: String(variantId)
        }
      };

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail
      }));

      try {
        sessionStorage.setItem('cart-count', JSON.stringify({
          value: String(itemCount),
          timestamp: Date.now()
        }));
      } catch (error) {
        // Cart synchronisation still works when session storage is blocked.
      }
    }

    async addToCart(button) {
      if (button.disabled) return;
      const variantId = Number.parseInt(button.dataset.variantId, 10);
      const errorNode = this.querySelector('[data-finder-error]');
      const successNode = this.querySelector('[data-finder-cart-success]');
      const detailsNode = this.querySelector('[data-finder-product-details]');
      const winner = this.favorite;
      button.disabled = true;
      button.textContent = this.addingLabel;
      if (errorNode) errorNode.hidden = true;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                '_Quelle': 'Azulejo-Finder',
                '_Raum': this.rooms.find((room) => room.id === this.state.roomId)?.label || ''
              }
            }]
          })
        });

        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        await response.json();

        const cartResponse = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        if (cart) this.notifyCartUpdated(cart, winner, variantId);

        this.state.addedToCart = true;
        this.saveState();
        button.classList.add('is-added');
        button.innerHTML = `<span aria-hidden="true">✓</span><span>${this.escapeHtml(this.addedLabel)}</span>`;
        if (successNode) successNode.hidden = false;
        if (detailsNode) detailsNode.hidden = true;

        this.dispatchEvent(new CustomEvent('azullado:finder-cart-added', {
          bubbles: true,
          detail: {
            productId: String(winner?.id || ''),
            variantId: String(variantId),
            quantity: 1
          }
        }));

        this.announce(this.addedLabel);
        this.track('Produkt hinzugefügt', winner?.shortTitle || 'Unbekanntes Produkt', 1, {
          productId: String(winner?.id || ''),
          productTitle: winner?.fullTitle || '',
          variantId: String(variantId),
          room: this.rooms.find((room) => room.id === this.state.roomId)?.label || ''
        });
      } catch (error) {
        console.error('Azulejo-Finder: Warenkorbfehler', error);
        button.disabled = false;
        button.textContent = this.addLabel;
        if (errorNode) {
          errorNode.textContent = this.cartError;
          errorNode.hidden = false;
        }
      }
    }

    async shareResult(button) {
      const winner = this.favorite;
      if (!winner) return;

      const room = this.rooms.find((item) => item.id === this.state.roomId) || null;
      const roomLabel = room?.label || '';
      const productUrl = this.absoluteUrl(winner.url);
      const finderUrl = this.absoluteUrl(window.location.pathname);
      const text = this.buildShareText(winner, roomLabel, productUrl, finderUrl);

      try {
        if (navigator.share) {
          const file = await this.createShareSceneFile(winner, room);

          if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: winner.fullTitle,
              text
            });
            this.track('Ergebnis geteilt', winner.shortTitle, undefined, {
              productId: String(winner.id),
              productTitle: winner.fullTitle,
              room: roomLabel,
              shareMode: 'scene_file'
            });
            return;
          }

          await navigator.share({
            title: winner.fullTitle,
            text
          });
          this.track('Ergebnis geteilt', winner.shortTitle, undefined, {
            productId: String(winner.id),
            productTitle: winner.fullTitle,
            room: roomLabel,
            shareMode: 'text_links'
          });
          return;
        }

        await this.copyToClipboard(text);
        this.showShareCopied(button);
        this.track('Link kopiert', winner.shortTitle, undefined, {
          productId: String(winner.id),
          productTitle: winner.fullTitle,
          room: roomLabel,
          shareMode: 'clipboard_text'
        });
      } catch (error) {
        if (error?.name === 'AbortError') return;
        try {
          await this.copyToClipboard(text);
          this.showShareCopied(button);
          this.track('Link kopiert', winner.shortTitle, undefined, {
            productId: String(winner.id),
            productTitle: winner.fullTitle,
            room: roomLabel,
            shareMode: 'clipboard_text'
          });
        } catch (clipboardError) {
          console.error('Azulejo-Finder: Teilen nicht möglich.', clipboardError);
        }
      }
    }

    buildShareText(winner, roomLabel, productUrl, finderUrl) {
      const template = this.copy['share-text'] || [
        'Mein Lieblings-Azulejo-Wandbild ist „[product]“.',
        '',
        'Zum Wandbild:',
        '[product_url]',
        '',
        'Finde deinen eigenen Favoriten:',
        '[finder_url]'
      ].join('\n');

      let text = this.formatTemplate(template, {
        product: winner.shortTitle,
        room: roomLabel || '',
        url: productUrl,
        product_url: productUrl,
        finder_url: finderUrl
      });

      if (!text.includes(productUrl)) {
        text = `${text.trim()}\n\nZum Wandbild:\n${productUrl}`;
      }

      if (!text.includes(finderUrl)) {
        text = `${text.trim()}\n\nFinde deinen eigenen Favoriten:\n${finderUrl}`;
      }

      return text.trim();
    }

    async createShareSceneFile(winner, room) {
      const scene = this.stage?.querySelector('.az-finder__room-scene');
      const backgroundNode = scene?.querySelector('.az-finder__room-background');
      const artworkFrameNode = scene?.querySelector('.az-finder__room-artwork');
      const artworkImageNode = artworkFrameNode?.querySelector('img');

      if (!scene || !artworkFrameNode || !artworkImageNode) return null;

      const sceneRect = scene.getBoundingClientRect();
      const frameRect = artworkFrameNode.getBoundingClientRect();
      const artworkRect = artworkImageNode.getBoundingClientRect();
      const backgroundRect = backgroundNode?.getBoundingClientRect() || null;

      if (!sceneRect.width || !sceneRect.height) return null;

      const canvasWidth = 1500;
      const canvasHeight = 1000;
      const scaleX = canvasWidth / sceneRect.width;
      const scaleY = canvasHeight / sceneRect.height;

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const context = canvas.getContext('2d');
      if (!context) return null;

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvasWidth, canvasHeight);

      try {
        if (backgroundNode?.src && backgroundRect) {
          const backgroundImage = await this.loadShareImage(backgroundNode.src);
          context.drawImage(
            backgroundImage,
            (backgroundRect.left - sceneRect.left) * scaleX,
            (backgroundRect.top - sceneRect.top) * scaleY,
            backgroundRect.width * scaleX,
            backgroundRect.height * scaleY
          );
        }

        const artworkImage = await this.loadShareImage(artworkImageNode.currentSrc || artworkImageNode.src || winner.image);
        context.save();
        context.beginPath();
        context.rect(
          (frameRect.left - sceneRect.left) * scaleX,
          (frameRect.top - sceneRect.top) * scaleY,
          frameRect.width * scaleX,
          frameRect.height * scaleY
        );
        context.clip();
        context.drawImage(
          artworkImage,
          (artworkRect.left - sceneRect.left) * scaleX,
          (artworkRect.top - sceneRect.top) * scaleY,
          artworkRect.width * scaleX,
          artworkRect.height * scaleY
        );
        context.restore();

        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/jpeg', 0.92);
        });
        if (!blob) return null;

        const safeProduct = (winner.handle || winner.shortTitle || 'azulejo').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const safeRoom = (room?.id || this.state.roomId || 'raum').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const fileName = `azullado-${safeProduct}-${safeRoom}.jpg`;
        return new File([blob], fileName, { type: 'image/jpeg' });
      } catch (error) {
        console.warn('Azulejo-Finder: Raumvisualisierung konnte nicht als Datei erstellt werden.', error);
        return null;
      }
    }

    loadShareImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.decoding = 'async';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
    }

    async copyToClipboard(value) {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
      }

      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }

    showShareCopied(button) {
      const label = button.querySelector('[data-finder-share-label]');
      if (!label) return;
      const original = label.textContent;
      label.textContent = this.copy['share-copied'] || 'Link kopiert';
      window.setTimeout(() => { label.textContent = original; }, 2200);
    }

    restart() {
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        // Ignore blocked storage.
      }
      this.track('Finder neu gestartet', this.state?.stage || 'unbekannt');
      this.lastTrackedResultId = null;
      this.state = this.createInitialState();
      this.saveState();
      this.render();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    absoluteUrl(value) {
      try {
        return new URL(value, window.location.origin).href;
      } catch (error) {
        return value;
      }
    }

    track(action, name = '', value, details = {}) {
      const customData = {
        ...details,
        event_action: String(action || ''),
        metric_name: String(name || ''),
        source: 'azulejo-finder'
      };

      if (Number.isFinite(value)) {
        customData.metric_value = value;
      }

      if (
        !window.Shopify?.designMode &&
        typeof window.Shopify?.analytics?.publish === 'function'
      ) {
        try {
          Promise.resolve(
            window.Shopify.analytics.publish('azullado:finder', customData)
          ).catch(() => {
            // Trackingfehler dürfen den Finder nicht beeinträchtigen.
          });
        } catch (error) {
          // Trackingfehler dürfen den Finder nicht beeinträchtigen.
        }
      }

      this.dispatchEvent(new CustomEvent('azullado:finder', {
        bubbles: true,
        detail: {
          action,
          name,
          value,
          ...details
        }
      }));
    }

    announce(message) {
      if (!this.announcement) return;
      this.announcement.textContent = '';
      window.setTimeout(() => { this.announcement.textContent = message; }, 20);
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }
  }

  if (!customElements.get('azulejo-finder')) {
    customElements.define('azulejo-finder', AzulejoFinder);
  }
})();
